import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Receipt, ShoppingCart, Truck, Factory, Package,
  Users, Building2, Wallet, Landmark, FileBarChart, Settings, Boxes, ClipboardList, Sparkles, ShieldCheck, FlaskConical, BarChart3, FileSpreadsheet, FileCheck2, BadgeCheck, Stamp, CalendarDays, FileSignature, HeartPulse, FileText, Plane, Ship, FileBox, FileInput, Anchor,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { divider: "Pilotage" },
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/assistant", label: "Assistant IA", icon: Sparkles },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3 },
  { to: "/rapports", label: "Rapports", icon: Boxes },
  { divider: "Achats" },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Truck },
  { to: "/achats", label: "Bons de commande", icon: ShoppingCart },
  { to: "/factures-fournisseurs", label: "Factures fournisseurs", icon: FileBarChart },
  { to: "/depenses", label: "Dépenses", icon: Receipt },
  { group: "Douane", icon: Ship, hr: true },
  { to: "/douane/d10", label: "D10 et reçus", icon: FileBox, sub: true, hr: true },
  { to: "/douane/transitaire", label: "Factures transitaire", icon: FileInput, sub: true, hr: true },
  { to: "/douane/frais", label: "Frais d'importation", icon: Anchor, sub: true, hr: true },
  { divider: "Ventes" },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/livraisons", label: "Bons de livraison", icon: Truck },
  { to: "/ventes", label: "Factures de ventes", icon: FileBarChart },
  { divider: "Stock & Production" },
  { to: "/stock", label: "Stock", icon: Package },
  { to: "/etat-stock", label: "État de stock", icon: Package },
  { to: "/inventaires", label: "Inventaires", icon: ClipboardList },
  { to: "/production", label: "Production", icon: Factory },
  { to: "/recettes", label: "Fiche technique", icon: FlaskConical },
  { to: "/immobilisations", label: "Immobilisations", icon: Building2 },
  { divider: "Trésorerie" },
  { to: "/caisse", label: "Caisse", icon: Wallet },
  { to: "/banque", label: "Banque", icon: Landmark },
  { divider: "Impôts" },
  { to: "/impots", label: "Impôts (G50)", icon: Receipt },
  { to: "/g50-etablis", label: "Préparer G50", icon: Stamp },
  { to: "/etablir-g50", label: "Écart G50", icon: FileCheck2 },
  { to: "/etat-104", label: "État 104", icon: FileSpreadsheet },
  { divider: "Employés", hr: true },
  { to: "/employes", label: "Dates d'embauche", icon: CalendarDays, end: true, hr: true },
  { to: "/employes/contrats", label: "Contrats de travail", icon: FileSignature, hr: true },
  { to: "/employes/sante", label: "Convention santé et analyses", icon: HeartPulse, hr: true },
  { to: "/employes/paie", label: "Fiches de paie", icon: FileText, hr: true },
  { to: "/employes/conges", label: "États des congés", icon: Plane, hr: true },
  { to: "/cnas", label: "Mise à jour CNAS", icon: BadgeCheck },
  { divider: "Administration" },
  { to: "/audit", label: "Journal d'activité", icon: ShieldCheck, adminDirection: true },
  { to: "/parametres", label: "Paramètres", icon: Settings },
];
const HR_ROLES = ["administrateur", "comptabilite", "direction"];

export function SidebarContent({ onNavigate }) {
  const { canViewAudit, role } = useAuth();
  const nav = NAV.filter((it) => (!it.adminDirection || (canViewAudit && canViewAudit())) && (!it.hr || HR_ROLES.includes(role)));
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border/70">
        <img src="/logo-gestion.jpeg" alt="Gestion" className="h-10 w-10 rounded-lg object-contain shrink-0" />
        <div className="leading-tight">
          <p className="font-heading font-extrabold text-foreground text-[15px]">General Food</p>
          <p className="text-[11px] text-muted-foreground">Gestion ERP</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {nav.map((item, i) => {
          if (item.group) {
            const G = item.icon;
            return (
              <div key={`g-${i}`} className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-muted-foreground" data-testid={`nav-group-${item.group.toLowerCase()}`}>
                <G className="h-[18px] w-[18px] shrink-0" /><span>{item.group}</span>
              </div>
            );
          }
          if (!item.to) {
            return (
              <div key={`d-${i}`} className="px-3 pt-4 pb-1.5">
                {item.divider && (
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                    {item.divider}
                  </p>
                )}
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`nav-${item.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
              className={({ isActive }) =>
                cn(
                  "sidebar-link flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
                  item.sub && "ml-6 py-1.5 text-[13px]",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span>{item.label}</span>
              {item.soon && <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70">bientôt</span>}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
